/**
 * Fields Management Component
 * 
 * Displays fields with drag-and-drop reordering, visibility toggle, and width controls.
 * Matches reference implementation: reference/app/src/modules/settings/routes/data-model/fields/components/fields-management.vue
 */

import { useState, useEffect, useMemo } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Eye, EyeOff, ChevronDown, FlipVerticalIcon, FlipHorizontalIcon } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { Field } from '@/api/fields';
import { updateField } from '@/api/fields';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { fieldsKeys } from '@/hooks/useFieldsQuery';

interface FieldsManagementProps {
    collection: string;
    fields: Field[];
    onFieldClick: (fieldName: string) => void;
    onFieldsReordered: () => void;
}

interface SortableFieldItemProps {
    field: Field;
    onFieldClick: (fieldName: string) => void;
    onToggleVisibility: (field: Field) => void;
    onSetWidth: (field: Field, width: 'half' | 'full') => void;
    disabled?: boolean;
}

function SortableFieldItem({
    field,
    onFieldClick,
    onToggleVisibility,
    onSetWidth,
    disabled = false,
}: SortableFieldItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: field.field, disabled });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const isHidden = field.meta?.hidden === true;
    const isPrimaryKey = field.schema?.is_primary_key === true;
    const isRequired = field.meta?.required === true;
    const width = field.meta?.width || 'full';
    const interfaceName = field.meta?.interface;

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={cn(
                'flex items-center justify-between p-3 rounded-lg border border-border bg-card transition-all',
                isDragging && 'opacity-50 shadow-lg z-50',
                isHidden && 'opacity-60',
                disabled && 'cursor-not-allowed'
            )}
        >
            <div className="flex items-center gap-3 flex-1 min-w-0">
                {!disabled && (
                    <button
                        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors touch-none"
                        {...attributes}
                        {...listeners}
                    >
                        <GripVertical className="h-5 w-5" />
                    </button>
                )}
                {disabled && (
                    <div className="w-5 h-5" />
                )}
                <button
                    className="flex items-center gap-3 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
                    onClick={() => !disabled && onFieldClick(field.field)}
                    disabled={disabled}
                >
                    <div className="flex h-8 w-8 items-center justify-center rounded bg-secondary shrink-0">
                        <span className="text-xs font-medium uppercase">
                            {field.type?.slice(0, 2) ?? '??'}
                        </span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <p className="font-medium text-sm truncate">
                                {field.name || field.field}
                            </p>
                            {isRequired && (
                                <span className="text-yellow-500" title="Required">
                                    ★
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate font-mono">
                            {field.field}
                            {interfaceName && ` · ${interfaceName}`}
                        </p>
                    </div>
                </button>
            </div>

            <div className="flex items-center gap-2 shrink-0">
                {/* Width indicator */}
                {width === 'half' ? (
                    <div className="text-xs text-muted-foreground px-2 py-1 rounded bg-secondary" title="Half Width">
                        1/2
                    </div>
                ) : (
                    <div className="text-xs text-muted-foreground px-2 py-1 rounded bg-secondary" title="Full Width">
                        Full
                    </div>
                )}

                {/* PK Badge */}
                {isPrimaryKey && (
                    <span className="rounded bg-primary px-2 py-0.5 text-xs text-primary-foreground">
                        PK
                    </span>
                )}

                {/* Visibility indicator */}
                {isHidden && (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                )}

                {/* Menu */}
                {!disabled && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className="p-1 hover:bg-accent rounded transition-colors">
                                <ChevronDown className="h-4 w-4" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onFieldClick(field.field)}>
                                Edit Field
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onToggleVisibility(field)}>
                                {isHidden ? (
                                    <>
                                        <Eye className="mr-2 h-4 w-4" />
                                        Show Field
                                    </>
                                ) : (
                                    <>
                                        <EyeOff className="mr-2 h-4 w-4" />
                                        Hide Field
                                    </>
                                )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                onClick={() => onSetWidth(field, 'half')}
                                disabled={width === 'half'}
                            >
                                <FlipVerticalIcon className="mr-2 h-4 w-4" />
                                Half Width
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => onSetWidth(field, 'full')}
                                disabled={width === 'full'}
                            >
                                <FlipHorizontalIcon className="mr-2 h-4 w-4" />
                                Full Width
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </div>
        </div>
    );
}

export function FieldsManagement({
    collection,
    fields,
    onFieldClick,
    onFieldsReordered,
}: FieldsManagementProps) {
    const queryClient = useQueryClient();

    // Separate system and user fields
    const systemFields = useMemo(() => {
        return fields
            .filter(f => f.meta?.system === true)
            .sort((a, b) => (a.meta?.sort || 0) - (b.meta?.sort || 0));
    }, [fields]);

    const userFields = useMemo(() => {
        return fields
            .filter(f => f.meta?.system !== true)
            .sort((a, b) => (a.meta?.sort || 0) - (b.meta?.sort || 0));
    }, [fields]);

    const [sortableFields, setSortableFields] = useState<Field[]>(userFields);

    // Update sortable fields when user fields change
    useEffect(() => {
        setSortableFields(userFields);
    }, [userFields]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = sortableFields.findIndex(f => f.field === active.id);
            const newIndex = sortableFields.findIndex(f => f.field === over.id);

            const reordered = arrayMove(sortableFields, oldIndex, newIndex);
            setSortableFields(reordered);

            // Update sort order in backend
            try {
                await Promise.all(
                    reordered.map((field, index) =>
                        updateField(collection, field.field, {
                            meta: {
                                ...field.meta,
                                sort: index + 1,
                            },
                        })
                    )
                );
                // Invalidate queries to force refresh
                await queryClient.invalidateQueries({ queryKey: fieldsKeys.collection(collection) });
                onFieldsReordered();
            } catch (error) {
                console.error('Error updating field order:', error);
                toast.error('Failed to update field order');
                // Revert on error
                setSortableFields(userFields);
            }
        }
    };

    const handleToggleVisibility = async (field: Field) => {
        try {
            const newHidden = !field.meta?.hidden;
            await updateField(collection, field.field, {
                meta: {
                    ...field.meta,
                    hidden: newHidden,
                },
            });
            toast.success(newHidden ? 'Field is now hidden' : 'Field is now visible');
            // Invalidate queries to force refresh
            await queryClient.invalidateQueries({ queryKey: fieldsKeys.collection(collection) });
            onFieldsReordered();
        } catch (error) {
            console.error('Error toggling field visibility:', error);
            toast.error('Failed to update field visibility');
        }
    };

    const handleSetWidth = async (field: Field, width: 'half' | 'full') => {
        try {
            await updateField(collection, field.field, {
                meta: {
                    ...field.meta,
                    width,
                },
            });
            toast.success(`Field width set to ${width}`);
            // Invalidate queries to force refresh
            await queryClient.invalidateQueries({ queryKey: fieldsKeys.collection(collection) });
            onFieldsReordered();
        } catch (error) {
            console.error('Error updating field width:', error);
            toast.error('Failed to update field width');
        }
    };

    return (
        <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
                Drag to reorder fields. The layout below shows how fields will appear in the form.
            </p>

            {/* System Fields (locked) */}
            {systemFields.length > 0 && (
                <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">System Fields (locked)</p>
                    <div className="grid grid-cols-2 gap-2">
                        {systemFields.map((field) => {
                            const width = field.meta?.width || 'full';
                            const widthClass = width === 'half' || width === 'half-left' || width === 'half-right'
                                ? 'col-span-1'
                                : 'col-span-2';

                            return (
                                <div key={field.field} className={widthClass}>
                                    <SortableFieldItem
                                        field={field}
                                        onFieldClick={onFieldClick}
                                        onToggleVisibility={handleToggleVisibility}
                                        onSetWidth={handleSetWidth}
                                        disabled
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* User Fields (draggable) */}
            {sortableFields.length > 0 ? (
                <div className="space-y-2">
                    {systemFields.length > 0 && (
                        <p className="text-sm text-muted-foreground">Custom Fields</p>
                    )}
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={sortableFields.map(f => f.field)}
                            strategy={verticalListSortingStrategy}
                        >
                            <div className="grid grid-cols-2 gap-2">
                                {sortableFields.map((field) => {
                                    const width = field.meta?.width || 'full';
                                    const widthClass = width === 'half' || width === 'half-left' || width === 'half-right'
                                        ? 'col-span-1'
                                        : 'col-span-2';

                                    return (
                                        <div key={field.field} className={widthClass}>
                                            <SortableFieldItem
                                                field={field}
                                                onFieldClick={onFieldClick}
                                                onToggleVisibility={handleToggleVisibility}
                                                onSetWidth={handleSetWidth}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </SortableContext>
                    </DndContext>
                </div>
            ) : (
                <div className="flex h-24 items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground">
                    No custom fields defined yet
                </div>
            )}
        </div>
    );
}
